/** Glossary (DESIGN UD-18): every term the interface defines in place, and every channel's explainer. Client-side search. */
import { useEffect, useState } from 'preact/hooks';
import { explainerFor } from '../../engine/channels/explain.ts';
import { CHANNEL_IDS } from '../../engine/channels/types.ts';
import { GLOSSARY_KEYS } from '../../shared/help-types.ts';
import { ExplainerLines, channelMeta, familyLabel } from '../components/Term.tsx';
import { route } from '../router.ts';
import { GLOSSARY } from '../strings-glossary.ts';
import { HELP } from '../strings-help.ts';

export function Glossary() {
	const [q, setQ] = useState('');
	const anchor = route.value.anchor;
	// deep links (`#/glossary#kappa`) scroll the entry into view once it is on screen
	useEffect(() => {
		if (!anchor) return;
		const raf = requestAnimationFrame(() => {
			document.getElementById(anchor)?.scrollIntoView({ block: 'start' });
		});
		return () => cancelAnimationFrame(raf);
	}, [anchor]);
	const needle = q.trim().toLowerCase();
	const terms = GLOSSARY_KEYS.flatMap((k) => {
		const e = GLOSSARY[k];
		if (!e) return [];
		if (needle && !`${e.term} ${e.definition} ${e.use ?? ''}`.toLowerCase().includes(needle))
			return [];
		return [{ k, e }];
	});
	const channels = CHANNEL_IDS.flatMap((id) => {
		const x = explainerFor(id);
		const meta = channelMeta(id);
		const hay = `${id} ${meta.name ?? ''} ${x?.measures ?? ''} ${x?.why ?? ''}`.toLowerCase();
		if (needle && !hay.includes(needle)) return [];
		return [{ id, meta }];
	});
	return (
		<div class="wrap" data-testid="glossary">
			<h1>{HELP.glossaryTitle}</h1>
			<section class="glossary-howto" aria-labelledby="howto-h">
				<h2 id="howto-h">{HELP.howToReadTitle}</h2>
				<p>{HELP.howToReadBody}</p>
			</section>
			<div class="glossary-search">
				<label class="field-label" for="glossary-q">
					{HELP.glossarySearch}
				</label>
				<input
					id="glossary-q"
					class="input"
					type="search"
					autocomplete="off"
					placeholder={HELP.glossarySearchPlaceholder}
					value={q}
					onInput={(e) => setQ((e.currentTarget as HTMLInputElement).value)}
				/>
			</div>
			{terms.length === 0 && channels.length === 0 ? (
				<output class="dim">{HELP.glossaryNone}</output>
			) : null}
			{terms.length > 0 ? (
				<section aria-labelledby="terms-h">
					<h2 id="terms-h">{HELP.glossaryTerms}</h2>
					<dl class="glossary-list">
						{terms.flatMap(({ k, e }) => [
							<dt key={`t-${k}`} id={k} class={anchor === k ? 'is-target' : undefined}>
								{e.term}
							</dt>,
							<dd key={`d-${k}`}>
								{e.definition}
								{e.use ? ` ${e.use}` : ''}
							</dd>,
						])}
					</dl>
				</section>
			) : null}
			{channels.length > 0 ? (
				<section aria-labelledby="channels-h">
					<h2 id="channels-h">{HELP.glossaryChannels}</h2>
					<p class="dim">{HELP.glossaryChannelsIntro}</p>
					{channels.map(({ id, meta }) => (
						<div key={id} id={id} class="glossary-channel">
							<h3>
								{[id, meta.name, meta.family ? familyLabel(meta.family) : undefined]
									.filter(Boolean)
									.join(' · ')}
							</h3>
							<ExplainerLines id={id} />
						</div>
					))}
				</section>
			) : null}
		</div>
	);
}
